import { trackDownload } from '../utils/downloadIndicator';
import React, { useState, useCallback, useEffect } from 'react';
import { 
  Box, Button, Paper, Typography, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Card, CardMedia,
  CardContent, CardActions, Avatar,
  Pagination, TextField, InputAdornment,
  Accordion, AccordionSummary, AccordionDetails,
  MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { filesApi, activitiesApi, formTemplatesApi, projectFormsApi, documentFoldersApi } from '../services/api';
import { renameFile } from '../utils/fileNaming';
import { PLAN_IMAGE_COMPRESSION_OPTIONS, compressImages } from '../utils/imageCompression';
import { limitFilesForUpload } from '../utils/uploadLimits';
import { convertPdfToImages, isPdf } from '../utils/pdfToImages';
import ImageViewer from './ImageViewer';
import ActivityDialog from './ActivityDialog';

// File size limit (10MB in bytes)
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const DEFAULT_SAS_URL_CACHE_TTL_MS = 4 * 60 * 1000;

// Accepted file types by folder
const ACCEPTED_FILE_TYPES = {
  photos: {
    'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp', '.heic', '.heif'],
  },
  plans: {
    'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp', '.heic', '.heif'],
    'application/pdf': ['.pdf'],
  },
  general: {
    'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp', '.heic', '.heif'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'text/csv': ['.csv'],
    'text/plain': ['.txt'],
  },
  supplier_quotes: {
    'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp', '.heic', '.heif'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'text/csv': ['.csv'],
    'text/plain': ['.txt'],
  },
  quote_workings: {
    'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp', '.heic', '.heif'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'text/csv': ['.csv'],
    'text/plain': ['.txt'],
  },
};

// Helper to get human-readable file type descriptions
const getAcceptedFileDescription = (fileType) => {
  const descriptions = {
    photos: 'Images only (JPG, PNG, GIF, WebP, HEIC)',
    plans: 'Images and PDFs (PDFs will be converted to images)',
    general: 'Images, PDFs, Word, Excel, PowerPoint, CSV, TXT',
    supplier_quotes: 'Images, PDFs, Word, Excel, PowerPoint, CSV, TXT',
    quote_workings: 'Images, PDFs, Word, Excel, PowerPoint, CSV, TXT',
  };
  return descriptions[fileType] || 'All supported files';
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB');
};

const getFileSortTimestamp = (file) => {
  const dateValue = file?.uploaded_date || file?.created_at;
  if (!dateValue) return 0;
  const timestamp = new Date(dateValue).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getDateFilterValue = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isWithinDateRange = (dateValue, startDate, endDate) => {
  if (!startDate && !endDate) return true;
  const normalizedDate = getDateFilterValue(dateValue);
  if (!normalizedDate) return false;
  if (startDate && normalizedDate < startDate) return false;
  if (endDate && normalizedDate > endDate) return false;
  return true;
};

const sortFilesByNewestUpload = (a, b) => {
  const timestampDiff = getFileSortTimestamp(b) - getFileSortTimestamp(a);
  if (timestampDiff !== 0) return timestampDiff;
  return (a.file_name || '').localeCompare(b.file_name || '');
};

// Helper function to format file type names for display
const formatFileTypeName = (fileType) => {
  const typeMap = {
    'supplier_quotes': 'Supplier Quotes',
    'quote_workings': 'Quote Workings',
    'plans': 'Plans',
    'photos': 'Photos',
    'general': 'General'
  };
  return typeMap[fileType] || fileType;
};

// Helper to get file icon based on type
const getFileIcon = (fileType, fileName) => {
  if (fileType === 'photos') return <ImageIcon />;
  if (fileType === 'plans') return <DescriptionIcon />;
  
  // For general files, try to determine by extension
  const ext = fileName.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return <ImageIcon />;
  } else if (['pdf', 'doc', 'docx'].includes(ext)) {
    return <DescriptionIcon />;
  }
  
  return <InsertDriveFileIcon />;
};

// Helper to check if file is an image
const isImageFile = (fileName) => {
  const ext = fileName.split('.').pop().toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
};

// Helper to check if file is a PDF
const isPdfFile = (fileName) => {
  const ext = fileName.split('.').pop().toLowerCase();
  return ext === 'pdf';
};

// Component for the dropzone
const UploadDropzone = ({ onDrop, fileType, label }) => {
  const acceptedTypes = ACCEPTED_FILE_TYPES[fileType] || ACCEPTED_FILE_TYPES.general;
  const fileDescription = getAcceptedFileDescription(fileType);
  const defaultLabel = `Drag and drop files here, or click to select files`;
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    accept: acceptedTypes,
  });
  
  return (
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : 'grey.400',
        borderRadius: 2,
        p: 2,
        textAlign: 'center',
        bgcolor: isDragActive ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
        cursor: 'pointer',
        mb: 2,
      }}
    >
      <input {...getInputProps()} />
      <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
      <Typography variant="body1">{label || defaultLabel}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {fileDescription}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Max file size: 10MB {fileType === 'plans'}
      </Typography>
    </Box>
  );
};

// Image thumbnail component
const LinkedRecordButtons = ({ file, onOpenActivity, onOpenForm, compact = false }) => {
  const activityLinks = Array.isArray(file.linked_activities) && file.linked_activities.length > 0
    ? file.linked_activities
    : (file.activity_id ? [{ activity_id: file.activity_id, title: file.activity_title }] : []);
  const formLinks = file.linked_forms || [];

  if (activityLinks.length === 0 && formLinks.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: compact ? 'column' : 'row', flexWrap: 'wrap', gap: compact ? 0 : 0.5, mt: 0.5 }}>
      {activityLinks.map((activity) => (
        <Button
          key={`activity-${activity.activity_id}`}
          size="small"
          variant="text"
          endIcon={<OpenInNewIcon sx={{ fontSize: '0.8rem !important' }} />}
          onClick={(event) => {
            event.stopPropagation();
            onOpenActivity?.(activity.activity_id);
          }}
          disabled={!onOpenActivity}
          title={`Open activity: ${activity.title || `Activity #${activity.activity_id}`}`}
          sx={{
            minWidth: 0,
            justifyContent: 'flex-start',
            p: 0,
            fontSize: compact ? '0.6rem' : '0.75rem',
            lineHeight: 1.3,
            textTransform: 'none',
          }}
        >
          Activity: {activity.title || `#${activity.activity_id}`}
        </Button>
      ))}
      {formLinks.map((form) => (
        <Button
          key={`form-${form.project_form_id}`}
          size="small"
          variant="text"
          endIcon={<OpenInNewIcon sx={{ fontSize: '0.8rem !important' }} />}
          onClick={(event) => {
            event.stopPropagation();
            onOpenForm?.(form.project_form_id);
          }}
          disabled={!onOpenForm}
          title={`Open form: ${form.title || form.template_name || `Form #${form.project_form_id}`}`}
          sx={{
            minWidth: 0,
            justifyContent: 'flex-start',
            p: 0,
            fontSize: compact ? '0.6rem' : '0.75rem',
            lineHeight: 1.3,
            textTransform: 'none',
          }}
        >
          Form: {form.title || form.template_name || `#${form.project_form_id}`}
        </Button>
      ))}
    </Box>
  );
};

const ImageThumbnail = ({ file, onPreview, onDownload, onDelete, onRename, onMove, onOpenActivity, onOpenForm, getFileUrl, size = 150 }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshAttempted, setRefreshAttempted] = useState(false);
  const hideFileName = file.file_type === 'photos';

  React.useEffect(() => {
    let active = true;
    const loadThumbnail = async () => {
      try {
        // Use the provided getFileUrl function if available, otherwise fallback (though it should be provided)
        const url = getFileUrl ? await getFileUrl(file.file_id) : (await filesApi.getUrl(file.file_id)).data.url;
        if (active) {
          setImageUrl(url);
        }
      } catch (error) {
        console.error('Error loading thumbnail:', error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    if (isImageFile(file.file_name)) {
      setRefreshAttempted(false);
      loadThumbnail();
    } else {
      setLoading(false);
    }
    
    return () => { active = false; };
  }, [file, getFileUrl]);

  const handleImageError = async () => {
    if (!getFileUrl || refreshAttempted) {
      setImageUrl(null);
      return;
    }

    setRefreshAttempted(true);
    setLoading(true);
    try {
      const refreshedUrl = await getFileUrl(file.file_id, true, true);
      setImageUrl(refreshedUrl);
    } catch (error) {
      console.error('Error refreshing thumbnail URL:', error);
      setImageUrl(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      data-testid={`file-card-${file.file_id}`}
      sx={{
        width: size,
        minHeight: hideFileName ? size + 68 : size + 84,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <CardMedia
        sx={{
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          position: 'relative',
          flexShrink: 0
        }}
        onClick={() => onPreview(file, 'file')}
      >
        {loading ? (
          <CircularProgress size={20} />
        ) : imageUrl && isImageFile(file.file_name) ? (
          <img
            src={imageUrl}
            alt={file.file_name}
            onError={handleImageError}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <Avatar sx={{ width: 50, height: 50, bgcolor: 'grey.300' }}>
            {getFileIcon(file.file_type, file.file_name)}
          </Avatar>
        )}
      </CardMedia>
      <CardContent sx={{ p: hideFileName ? 0.5 : 0.75, pb: hideFileName ? 0.5 : 0.75, flexGrow: 1, flexShrink: 0, overflow: 'hidden' }}>
        {!hideFileName && (
          <Typography 
            variant="caption" 
            sx={{ 
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '0.65rem',
              fontWeight: 500,
              wordBreak: 'break-word'
            }}
            title={file.file_name}
          >
            {file.file_name}
          </Typography>
        )}
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block',
            fontSize: '0.6rem',
            color: 'text.secondary',
            lineHeight: 1.2,
            mt: hideFileName ? 0 : 0.25
          }}
        >
          {formatDate(file.uploaded_date)}
        </Typography>
        <LinkedRecordButtons
          file={file}
          onOpenActivity={onOpenActivity}
          onOpenForm={onOpenForm}
          compact
        />
      </CardContent>
      <CardActions sx={{ px: 1, pb: 0.5, pt: 0, justifyContent: 'flex-end', gap: 0.5 }}>
        {onMove && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onMove(file); }}
            title="Move to folder"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: 'action.hover' }
            }}
          >
            <DriveFileMoveIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton 
          size="small" 
          onClick={(e) => { e.stopPropagation(); onRename(file); }} 
          title="Rename"
          sx={{ 
            color: 'text.secondary',
            '&:hover': { color: 'primary.main', bgcolor: 'action.hover' }
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton 
          size="small" 
          onClick={(e) => { e.stopPropagation(); onDelete(file, 'file'); }} 
          title="Delete"
          sx={{ 
            color: 'text.secondary',
            '&:hover': { color: 'error.main', bgcolor: 'action.hover' }
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );
};

// Folder view component
const FolderView = ({ 
  title, 
  files, 
  onPreview, 
  onDownload, 
  onDelete, 
  onRename,
  onMove,
  onOpenActivity,
  onOpenForm,
  getFileUrl,
  searchTerm, 
  uploadStartDateFilter,
  uploadEndDateFilter,
  viewMode = 'grid',
  emptyMessage = 'No files uploaded',
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = viewMode === 'grid' ? 12 : 10;
  const hasActiveFilters = Boolean(searchTerm || uploadStartDateFilter || uploadEndDateFilter);
  
  const filteredFiles = [...files]
    .sort(sortFilesByNewestUpload)
    .filter(file =>
      file.file_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      isWithinDateRange(
        file.uploaded_date || file.created_at,
        uploadStartDateFilter,
        uploadEndDateFilter
      )
    );

  useEffect(() => {
    setCurrentPage(1);
  }, [files, searchTerm, uploadStartDateFilter, uploadEndDateFilter, viewMode]);
  
  const totalPages = Math.ceil(filteredFiles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedFiles = filteredFiles.slice(startIndex, startIndex + itemsPerPage);
  
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  if (viewMode === 'grid') {
    return (
      <Box>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
          gap: 1.5,
          mb: 2
        }}>
          {paginatedFiles.map((file) => (
            <ImageThumbnail
              key={file.file_id}
              file={file}
              onPreview={(selectedFile) => onPreview(selectedFile, filteredFiles)}
              onDownload={onDownload}
              onDelete={onDelete}
              onRename={onRename}
              onMove={onMove}
              onOpenActivity={onOpenActivity}
              onOpenForm={onOpenForm}
              getFileUrl={getFileUrl}
            />
          ))}
        </Box>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
        {filteredFiles.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {hasActiveFilters ? 'No files match your filters' : emptyMessage}
            </Typography>
          </Paper>
        )}
      </Box>
    );
  }

  // List view
  return (
    <Box>
      <Paper sx={{ mt: 1 }}>
        {paginatedFiles.map((file, index) => (
          <Box
            key={file.file_id}
            onClick={() => onPreview(file, filteredFiles)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              cursor: 'pointer',
              borderBottom: index < paginatedFiles.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ mr: 2 }}>
              {getFileIcon(file.file_type, file.file_name)}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body1">{file.file_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(file.uploaded_date)}
              </Typography>
              <LinkedRecordButtons
                file={file}
                onOpenActivity={onOpenActivity}
                onOpenForm={onOpenForm}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {onMove && (
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onMove(file); }}
                  title="Move to folder"
                >
                  <DriveFileMoveIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton 
                size="small" 
                onClick={(e) => { e.stopPropagation(); onRename(file); }}
                title="Rename"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={(e) => { e.stopPropagation(); onDelete(file); }}
                title="Delete"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Paper>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
      {filteredFiles.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {hasActiveFilters ? 'No files match your filters' : emptyMessage}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

const ProjectDocumentsTab = ({ projectId, files, refreshFiles, currentUser, workers = [], onOpenActivity, onOpenForm }) => {
  // Cache and Queue State for image loading
  const urlCache = React.useRef(new Map());
  const pendingRequests = React.useRef(new Map());
  const requestQueue = React.useRef([]);
  const activeRequests = React.useRef(0);
  const queuePaused = React.useRef(false);
  const MAX_CONCURRENT = 3;

  const cacheFileUrl = useCallback((fileId, url, expiresInSeconds) => {
    const ttlMs = expiresInSeconds
      ? Math.max(30 * 1000, (Number(expiresInSeconds) * 1000) - 30 * 1000)
      : DEFAULT_SAS_URL_CACHE_TTL_MS;
    urlCache.current.set(fileId, {
      url,
      expiresAt: Date.now() + ttlMs,
    });
  }, []);

  const getCachedFileUrl = useCallback((fileId) => {
    const cached = urlCache.current.get(fileId);
    if (!cached) return null;

    if (typeof cached === 'string') {
      urlCache.current.delete(fileId);
      return null;
    }

    if (cached.expiresAt <= Date.now()) {
      urlCache.current.delete(fileId);
      return null;
    }

    return cached.url;
  }, []);

  const processQueue = useCallback(() => {
    if (queuePaused.current || activeRequests.current >= MAX_CONCURRENT || requestQueue.current.length === 0) return;

    activeRequests.current++;
    const { fileId, resolve, reject } = requestQueue.current.shift();

    filesApi.getUrl(fileId)
      .then(response => {
        const url = response.data.url;
        cacheFileUrl(fileId, url, response.data.expires_in_seconds);
        resolve(url);
      })
      .catch(reject)
      .finally(() => {
        activeRequests.current--;
        pendingRequests.current.delete(fileId);
        processQueue();
      });
  }, [cacheFileUrl]);

  const getFileUrl = useCallback((fileId, priority = false, forceRefresh = false) => {
    // 1. Check Cache
    if (!forceRefresh) {
      const cachedUrl = getCachedFileUrl(fileId);
      if (cachedUrl) {
        return Promise.resolve(cachedUrl);
      }
    } else {
      urlCache.current.delete(fileId);
    }

    // 2. Check Pending (In-flight or Queued)
    if (pendingRequests.current.has(fileId)) {
      // If it's a priority request, and the item is currently sitting in the queue (not active),
      // we should pull it out and run it immediately.
      if (priority) {
        const queueIndex = requestQueue.current.findIndex(item => item.fileId === fileId);
        if (queueIndex !== -1) {
          // Found in queue! Remove it.
          const [item] = requestQueue.current.splice(queueIndex, 1);
          
          // Execute immediately (bypassing concurrency limit for priority user action)
          filesApi.getUrl(fileId)
            .then(response => {
              const url = response.data.url;
              cacheFileUrl(fileId, url, response.data.expires_in_seconds);
              item.resolve(url);
            })
            .catch(item.reject)
            .finally(() => {
               pendingRequests.current.delete(fileId);
               // We don't need to call processQueue here because we didn't consume a slot
            });
            
           return pendingRequests.current.get(fileId); // Return the existing promise
        }
      }
      return pendingRequests.current.get(fileId);
    }

    // 3. New Priority Request
    if (priority) {
      // Pause background queue to prioritize this request
      queuePaused.current = true;
      
      const promise = filesApi.getUrl(fileId)
        .then(response => {
          const url = response.data.url;
          cacheFileUrl(fileId, url, response.data.expires_in_seconds);
          return url;
        })
        .finally(() => {
          pendingRequests.current.delete(fileId);
          // Resume background queue
          queuePaused.current = false;
          processQueue();
        });
      
      pendingRequests.current.set(fileId, promise);
      return promise;
    }

    // 4. Queue Request
    const promise = new Promise((resolve, reject) => {
      requestQueue.current.push({ fileId, resolve, reject });
      processQueue();
    });
    
    pendingRequests.current.set(fileId, promise);
    return promise;
  }, [cacheFileUrl, getCachedFileUrl, processQueue]);

  // Preload next/prev images for smoother navigation
  const preloadNeighbors = useCallback((currentFileId, candidateFiles = null) => {
    const currentFile = files.find(f => f.file_id === currentFileId);
    if (!currentFile) return;

    const imageFiles = (candidateFiles || files)
      .filter(f => isImageFile(f.file_name) && f.file_type === currentFile.file_type);
    const currentIndex = imageFiles.findIndex(f => f.file_id === currentFileId);
    if (currentIndex === -1 || imageFiles.length < 2) return;

    const nextIndex = (currentIndex + 1) % imageFiles.length;
    const prevIndex = (currentIndex - 1 + imageFiles.length) % imageFiles.length;
    
    // Load next and prev (standard priority, let them go into queue)
    getFileUrl(imageFiles[nextIndex].file_id);
    getFileUrl(imageFiles[prevIndex].file_id);
  }, [files, getFileUrl]);
  
  const [uploadType, setUploadType] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [newFileName, setNewFileName] = useState('');
  const [renameError, setRenameError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadStartDateFilter, setUploadStartDateFilter] = useState('');
  const [uploadEndDateFilter, setUploadEndDateFilter] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [documentFolders, setDocumentFolders] = useState([]);
  const [selectedFolderIds, setSelectedFolderIds] = useState({});
  const [uploadFolderId, setUploadFolderId] = useState('');
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [folderDialogMode, setFolderDialogMode] = useState('create');
  const [folderName, setFolderName] = useState('');
  const [folderError, setFolderError] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState(false);
  const [moveFileDialogOpen, setMoveFileDialogOpen] = useState(false);
  const [fileToMove, setFileToMove] = useState(null);
  const [moveFolderId, setMoveFolderId] = useState('');
  const [folderFileType, setFolderFileType] = useState('');
  
  // Preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFileName, setPreviewFileName] = useState('');
  const [previewUploadedDate, setPreviewUploadedDate] = useState('');
  const [allPreviewFiles, setAllPreviewFiles] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [currentFileId, setCurrentFileId] = useState(null);
  
  // Pin state (for plan images)
  const [pins, setPins] = useState([]);
  const [pinMode, setPinMode] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [pendingPin, setPendingPin] = useState(null); // {x, y} coordinates
  const [selectedPin, setSelectedPin] = useState(null);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [activityDeleteDialogOpen, setActivityDeleteDialogOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [deletingActivity, setDeletingActivity] = useState(false);
  const [newActivityData, setNewActivityData] = useState({
    title: '',
    description: '',
    due_date: null,
    is_completed: false,
    assigned_workers: [],
    existing_photos: [],
    existing_documents: [],
    pending_file_links: [],
    linked_forms: [],
    pending_form_templates: [],
    removed_photo_ids: [],
    removed_document_ids: [],
    removed_form_ids: [],
  });
  const [formTemplates, setFormTemplates] = useState([]);
  
  // Dialog photos state (for activity creation/editing)
  const [dialogPhotos, setDialogPhotos] = useState([]);
  
  const [loading, setLoading] = useState(false);
  
  // File types for documents - filtered based on user role
  const getAllFileTypes = () => ['plans', 'photos', 'general', 'supplier_quotes', 'quote_workings'];
  const getFileTypesForRole = () => {
    if (currentUser?.role === 'Site' || currentUser?.role === 'Site Manager') {
      return ['plans', 'photos'];
    }
    return getAllFileTypes();
  };
  
  const fileTypes = getFileTypesForRole();

  useEffect(() => {
    formTemplatesApi
      .getAll()
      .then((response) => setFormTemplates(response.data || []))
      .catch((error) => {
        console.error('Error loading form templates:', error);
      });
  }, []);

  const refreshDocumentFolders = useCallback(async () => {
    try {
      const response = await documentFoldersApi.getByProjectId(projectId);
      const nextFolders = response.data || [];
      setDocumentFolders((currentFolders) => {
        const isUnchanged = currentFolders.length === nextFolders.length
          && currentFolders.every((folder, index) => (
            folder.document_folder_id === nextFolders[index]?.document_folder_id
            && folder.file_type === nextFolders[index]?.file_type
            && folder.name === nextFolders[index]?.name
            && folder.file_count === nextFolders[index]?.file_count
          ));
        return isUnchanged ? currentFolders : nextFolders;
      });
    } catch (error) {
      console.error('Error loading document folders:', error);
    }
  }, [projectId]);

  useEffect(() => {
    refreshDocumentFolders();
  }, [refreshDocumentFolders]);
  
  const handleOpenUploadDialog = (fileType, documentFolderId = '') => {
    setUploadType(fileType);
    setUploadFolderId(documentFolderId !== null ? documentFolderId : '');
    setUploadDialogOpen(true);
  };
  
  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setUploadFolderId('');
  };

  const getFolderErrorMessage = (error, fallback) => (
    error?.response?.data?.error || fallback
  );

  const handleOpenCreateFolderDialog = (fileType) => {
    setFolderDialogMode('create');
    setFolderFileType(fileType);
    setSelectedFolder(null);
    setFolderName('');
    setFolderError('');
    setFolderDialogOpen(true);
  };

  const handleOpenRenameFolderDialog = (folder) => {
    setFolderDialogMode('rename');
    setFolderFileType(folder.file_type);
    setSelectedFolder(folder);
    setFolderName(folder.name);
    setFolderError('');
    setFolderDialogOpen(true);
  };

  const handleCloseFolderDialog = () => {
    setFolderDialogOpen(false);
    setSelectedFolder(null);
    setFolderName('');
    setFolderError('');
  };

  const handleSaveFolder = async () => {
    const normalizedName = folderName.trim().replace(/\s+/g, ' ');
    if (!normalizedName) {
      setFolderError('Folder name is required');
      return;
    }
    if (normalizedName.length > 100) {
      setFolderError('Folder name must be 100 characters or fewer');
      return;
    }
    const hasControlCharacter = Array.from(normalizedName)
      .some((character) => character.charCodeAt(0) < 32);
    if (normalizedName.includes('/') || normalizedName.includes('\\') || hasControlCharacter) {
      setFolderError('Folder name cannot contain slashes or control characters');
      return;
    }

    try {
      setLoading(true);
      if (folderDialogMode === 'rename') {
        await documentFoldersApi.rename(selectedFolder.document_folder_id, { name: normalizedName });
      } else {
        await documentFoldersApi.create(projectId, {
          name: normalizedName,
          file_type: folderFileType,
        });
      }
      await refreshDocumentFolders();
      handleCloseFolderDialog();
    } catch (error) {
      setFolderError(getFolderErrorMessage(error, 'Failed to save folder'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteFolderDialog = (folder) => {
    setSelectedFolder(folder);
    setDeleteFolderDialogOpen(true);
  };

  const handleDeleteFolder = async () => {
    if (!selectedFolder) return;
    try {
      setLoading(true);
      await documentFoldersApi.delete(selectedFolder.document_folder_id);
      setSelectedFolderIds((current) => (
        current[selectedFolder.file_type] === selectedFolder.document_folder_id
          ? { ...current, [selectedFolder.file_type]: null }
          : current
      ));
      await Promise.all([refreshDocumentFolders(), refreshFiles()]);
      setDeleteFolderDialogOpen(false);
      setSelectedFolder(null);
    } catch (error) {
      console.error('Error deleting document folder:', error);
      alert(getFolderErrorMessage(error, 'Failed to delete folder'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMoveFileDialog = (file) => {
    setFileToMove(file);
    setMoveFolderId(file.document_folder_id ?? file.photo_folder_id ?? '');
    setMoveFileDialogOpen(true);
  };

  const handleMoveFile = async () => {
    if (!fileToMove) return;
    try {
      setLoading(true);
      await filesApi.moveToDocumentFolder(
        fileToMove.file_id,
        moveFolderId === '' ? null : Number(moveFolderId)
      );
      await Promise.all([refreshFiles(), refreshDocumentFolders()]);
      setMoveFileDialogOpen(false);
      setFileToMove(null);
      setMoveFolderId('');
    } catch (error) {
      console.error('Error moving file:', error);
      alert(getFolderErrorMessage(error, 'Failed to move file'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenDeleteDialog = (file) => {
    setSelectedFile(file);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleOpenRenameDialog = (file) => {
    setSelectedFile(file);
    setNewFileName(file.file_name);
    setRenameError('');
    setRenameDialogOpen(true);
  };
  
  const handleCloseRenameDialog = () => {
    setRenameDialogOpen(false);
    setRenameError('');
  };
  
  const handleRename = async () => {
    // Trim whitespace
    const trimmedName = newFileName.trim();
    
    if (!trimmedName) {
      setRenameError('File name cannot be empty');
      return;
    }
    
    // Check if new name is the same as current name
    if (trimmedName === selectedFile.file_name) {
      handleCloseRenameDialog();
      return;
    }
    
    // Check for naming conflicts in the same project
    const fileNameExists = files.some(f => 
      f.file_name.toLowerCase() === trimmedName.toLowerCase() && 
      f.file_id !== selectedFile.file_id
    );
    
    if (fileNameExists) {
      setRenameError(`A file named "${trimmedName}" already exists in this project`);
      return;
    }
    
    try {
      setLoading(true);
      await filesApi.rename(selectedFile.file_id, trimmedName);
      refreshFiles();
      handleCloseRenameDialog();
    } catch (error) {
      console.error('Error renaming file:', error);
      setRenameError('Failed to rename file. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    try {
      await filesApi.delete(selectedFile.file_id);
      refreshFiles();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };
  
  const handleDownload = trackDownload(async (file) => {
    try {
      const urlResponse = await filesApi.getUrl(file.file_id);
      const fileUrl = urlResponse.data.url;

      // To avoid CORS issues when fetching cross-origin (Azure Blob SAS URLs),
      // open the file URL in a new tab/window which is not subject to fetch CORS.
      // This preserves the browser's native handling (download or display).
      const newWindow = window.open(fileUrl, '_blank');
      if (!newWindow) {
        // Popup blocked — fallback to creating an anchor and clicking it.
        const link = document.createElement('a');
        link.href = fileUrl;
        link.target = '_blank';
        // `download` attribute is ignored for cross-origin URLs in many browsers,
        // so we don't set it to avoid misleading behavior.
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      // Final fallback: try to reacquire URL and navigate directly
      try {
        const urlResponse = await filesApi.getUrl(file.file_id);
        const fileUrl = urlResponse.data.url;
        window.location.href = fileUrl;
      } catch (err) {
        console.error('Error downloading file fallback:', err);
        alert('Error downloading file: ' + (err.message || err));
      }
    }
  });

  const handlePreview = async (file, previewScopeFiles = null) => {
    // If it's a PDF, handle it differently
    if (isPdfFile(file.file_name)) {
      // Open window immediately to maintain user interaction context
      // This is crucial for mobile browsers to prevent popup blocking
      const newWindow = window.open('', '_blank');
      
      if (newWindow) {
        // Set a loading message
        newWindow.document.write(`
          <html>
            <head><title>Loading...</title></head>
            <body style="display:flex;justify-content:center;align-items:center;height:100vh;margin:0;font-family:sans-serif;">
              <div style="text-align:center;">
                <p>Loading PDF...</p>
              </div>
            </body>
          </html>
        `);
        await handleOpenPdf(file, newWindow);
      } else {
        // Fallback if popup blocker caught it anyway
        alert('Please allow popups to view this file');
      }
      return;
    }
      // If the file is not an image (and not a PDF which was handled above), trigger a download
      if (!isImageFile(file.file_name)) {
        handleDownload(file);
        return;
      }
    
      // Check cache first to avoid loading state if possible
    const cachedUrl = getCachedFileUrl(file.file_id);
    if (cachedUrl) {
      const imageFiles = (previewScopeFiles || files)
        .filter(f => isImageFile(f.file_name) && f.file_type === file.file_type);
      const currentIndex = imageFiles.findIndex(f => f.file_id === file.file_id);
      
      setPreviewUrl(cachedUrl);
      setPreviewFileName(file.file_name);
      setPreviewUploadedDate(file.uploaded_date);
      setAllPreviewFiles(imageFiles);
      setCurrentPreviewIndex(currentIndex >= 0 ? currentIndex : 0);
      setCurrentFileId(file.file_id);
      
      // Load pins if this is a plan image (with loading indicator)
      setPreviewDialogOpen(true);
      if (file.file_type === 'plans') {
        setLoading(true);
        try {
          const pinsResponse = await activitiesApi.getByFileId(file.file_id);
          setPins(pinsResponse.data || []);
        } catch (error) {
          console.error('Error loading pins:', error);
          setPins([]);
        } finally {
          setLoading(false);
        }
      } else {
        setPins([]);
      }
      
      preloadNeighbors(file.file_id, imageFiles);
      return;
    }

    setLoading(true);
    try {
      // Use getFileUrl with priority=true
      const url = await getFileUrl(file.file_id, true);
      
      if (!url) {
        console.error('No URL returned from the server');
        setLoading(false);
        return;
      }
      
      // Get all image files for navigation (exclude PDFs and other non-viewable formats)
      const imageFiles = (previewScopeFiles || files)
        .filter(f => isImageFile(f.file_name) && f.file_type === file.file_type);
      const currentIndex = imageFiles.findIndex(f => f.file_id === file.file_id);
      
      setPreviewUrl(url);
      setPreviewFileName(file.file_name);
      setPreviewUploadedDate(file.uploaded_date);
      setAllPreviewFiles(imageFiles);
      setCurrentPreviewIndex(currentIndex >= 0 ? currentIndex : 0);
      setCurrentFileId(file.file_id);
      
      setPreviewDialogOpen(true);
      
      // Load pins if this is a plan image (with loading indicator)
      if (file.file_type === 'plans') {
        try {
          const pinsResponse = await activitiesApi.getByFileId(file.file_id);
          console.log('Loaded pins for file', file.file_id, ':', pinsResponse.data);
          setPins(pinsResponse.data || []);
        } catch (error) {
          console.error('Error loading pins:', error);
          setPins([]);
        }
      } else {
        setPins([]);
      }
      
      preloadNeighbors(file.file_id, imageFiles);
    } catch (error) {
      console.error('Error previewing file:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenPdf = async (file, targetWindow) => {
    setLoading(true);
    try {
      // Use getFileUrl with priority=true
      const url = await getFileUrl(file.file_id, true);
      
      if (!url) {
        throw new Error('No URL returned from the server');
      }
      
      if (targetWindow) {
        targetWindow.location.href = url;
      }
    } catch (error) {
      console.error('Error opening PDF:', error);
      if (targetWindow) {
        targetWindow.close();
      }
      alert('Failed to open PDF. Please try downloading the file instead.');
    } finally {
      setLoading(false);
    }
  };

  const handleClosePreviewDialog = () => {
    setPreviewDialogOpen(false);
    setPreviewUrl('');
    setPreviewUploadedDate('');
    setAllPreviewFiles([]);
    setCurrentPreviewIndex(0);
    setCurrentFileId(null);
    setPins([]);
    setPinMode(false);
    setSelectedPin(null);
  };

  const navigatePreviewPhoto = async (direction) => {
    const newIndex = direction === 'next' 
      ? (currentPreviewIndex + 1) % allPreviewFiles.length
      : (currentPreviewIndex - 1 + allPreviewFiles.length) % allPreviewFiles.length;
    
    const nextFile = allPreviewFiles[newIndex];
    
    // Check cache first
    const cachedUrl = getCachedFileUrl(nextFile.file_id);
    if (cachedUrl) {
      setPreviewUrl(cachedUrl);
      setPreviewFileName(nextFile.file_name);
      setPreviewUploadedDate(nextFile.uploaded_date);
      setCurrentPreviewIndex(newIndex);
      setCurrentFileId(nextFile.file_id);
      
      // Load pins if this is a plan image (with loading indicator)
      if (nextFile.file_type === 'plans') {
        setLoading(true);
        try {
          const pinsResponse = await activitiesApi.getByFileId(nextFile.file_id);
          setPins(pinsResponse.data || []);
        } catch (error) {
          console.error('Error loading pins:', error);
          setPins([]);
        } finally {
          setLoading(false);
        }
      } else {
        setPins([]);
      }
      
      preloadNeighbors(nextFile.file_id, allPreviewFiles);
      return;
    }

    setLoading(true);
    
    try {
      // Use getFileUrl with priority=true
      const url = await getFileUrl(nextFile.file_id, true);
      
      if (url) {
        setPreviewUrl(url);
        setPreviewFileName(nextFile.file_name);
        setPreviewUploadedDate(nextFile.uploaded_date);
        setCurrentPreviewIndex(newIndex);
        setCurrentFileId(nextFile.file_id);
        
        // Load pins if this is a plan image (with loading indicator)
        if (nextFile.file_type === 'plans') {
          try {
            const pinsResponse = await activitiesApi.getByFileId(nextFile.file_id);
            console.log('Loaded pins for file', nextFile.file_id, ':', pinsResponse.data);
            setPins(pinsResponse.data || []);
          } catch (error) {
            console.error('Error loading pins:', error);
            setPins([]);
          }
        } else {
          setPins([]);
        }
        
        preloadNeighbors(nextFile.file_id, allPreviewFiles);
      }
    } catch (error) {
      console.error('Error loading next photo:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePreviewDelete = (file) => {
    handleOpenDeleteDialog(file);
  };

  // Normalize file type for backend compatibility
  const normalizeUploadType = (type) => {
    // If your backend expects different keys, adjust here
    // For example, if it expects 'supplierQuotes' instead of 'supplier_quotes', map it here
    const map = {
      supplier_quotes: 'supplier_quotes',
      quote_workings: 'quote_workings',
      plans: 'plans',
      photos: 'photos',
      general: 'general',
    };
    return map[type] || type;
  };

  const handleFileDrop = async (droppedFiles) => {
    const { files: acceptedFiles, warning } = limitFilesForUpload(droppedFiles || []);
    if (warning) {
      alert(warning);
      return;
    }
    
    setLoading(true);
    if (!uploadType) { setLoading(false); return; }
    
    try {
      console.log('Files before processing:', acceptedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })));
      
      // Separate PDFs (for plans conversion) from other files
      const pdfFiles = uploadType === 'plans' ? acceptedFiles.filter(f => isPdf(f)) : [];
      const nonPdfFiles = uploadType === 'plans' ? acceptedFiles.filter(f => !isPdf(f)) : acceptedFiles;
      
      // Validate file sizes (except PDFs going to plans folder - they get converted)
      const oversizedFiles = nonPdfFiles.filter(f => f.size > MAX_FILE_SIZE_BYTES);
      if (oversizedFiles.length > 0) {
        const fileNames = oversizedFiles.map(f => `${f.name} (${(f.size / 1024 / 1024).toFixed(2)}MB)`).join(', ');
        alert(`The following files exceed the 10MB limit and cannot be uploaded:\n${fileNames}`);
        setLoading(false);
        return;
      }
      
      // Also check non-plans PDFs for size limit
      if (uploadType !== 'plans') {
        const oversizedPdfs = acceptedFiles.filter(f => isPdf(f) && f.size > MAX_FILE_SIZE_BYTES);
        if (oversizedPdfs.length > 0) {
          const fileNames = oversizedPdfs.map(f => `${f.name} (${(f.size / 1024 / 1024).toFixed(2)}MB)`).join(', ');
          alert(`The following PDF files exceed the 10MB limit:\n${fileNames}`);
          setLoading(false);
          return;
        }
      }
      
      let allFilesToUpload = [];
      
      // Convert PDFs to images for plans folder
      if (pdfFiles.length > 0) {
        console.log(`Converting ${pdfFiles.length} PDF(s) to images...`);
        for (const pdfFile of pdfFiles) {
          try {
            const imageFiles = await convertPdfToImages(pdfFile, (page, total) => {
              console.log(`Converting PDF page ${page}/${total}`);
            });
            allFilesToUpload.push(...imageFiles);
          } catch (pdfError) {
            console.error(`Error converting PDF ${pdfFile.name}:`, pdfError);
            alert(`Failed to convert PDF "${pdfFile.name}": ${pdfError.message}`);
          }
        }
      }
      
      // Compress images before uploading (non-images pass through unchanged)
      const compressionOptions = uploadType === 'plans' ? PLAN_IMAGE_COMPRESSION_OPTIONS : {};
      const compressedNonPdfFiles = await compressImages(nonPdfFiles, compressionOptions);
      allFilesToUpload.push(...compressedNonPdfFiles);
      
      console.log('Files after processing:', allFilesToUpload.map(f => ({ name: f.name, size: f.size, type: f.type })));
      
      // Rename files with UUID before uploading
      const renamedFiles = allFilesToUpload.map(file => renameFile(file));
      
      // Normalize the uploadType before sending to backend
      const normalizedType = normalizeUploadType(uploadType);
      // Upload all files in parallel
      await Promise.all(
        renamedFiles.map(file => filesApi.upload(
          projectId,
          file,
          normalizedType,
          uploadFolderId !== ''
            ? Number(uploadFolderId)
            : null
        ))
      );
      await Promise.all([refreshFiles(), refreshDocumentFolders()]);
      handleCloseUploadDialog();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading files: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Filter files by type
  const getFilesByType = (type) => {
    return files
      .filter(file => file.file_type === type)
      .sort(sortFilesByNewestUpload);
  };

  // Pin-related handlers
  const handleTogglePinMode = () => {
    setPinMode(!pinMode);
  };

  const handleAddPin = (pinX, pinY) => {
    // Store the pending pin coordinates and open activity dialog
    setPendingPin({ x: pinX, y: pinY });
    setNewActivityData({
      title: '',
      description: '',
      due_date: null,
      is_completed: false,
      assigned_workers: [],
      existing_photos: [],
      existing_documents: [],
      pending_file_links: [],
      linked_forms: [],
      pending_form_templates: [],
      removed_photo_ids: [],
      removed_document_ids: [],
      removed_form_ids: [],
    });
    setActivityDialogOpen(true);
  };

  const handlePinClick = (pin) => {
    console.log('Pin clicked:', pin);
    setSelectedPin(pin);
  };

  const handleCloseActivityDialog = () => {
    setActivityDialogOpen(false);
    setPendingPin(null);
    setEditingActivityId(null);
    setDialogPhotos([]); // Clear photos
    setNewActivityData({
      title: '',
      description: '',
      due_date: null,
      is_completed: false,
      assigned_workers: [],
      existing_photos: [],
      existing_documents: [],
      pending_file_links: [],
      linked_forms: [],
      pending_form_templates: [],
      removed_photo_ids: [],
      removed_document_ids: [],
      removed_form_ids: [],
    });
  };

  const handleDialogPhotoSelect = useCallback(async (event) => {
    const { files: selectedFiles, warning } = limitFilesForUpload(event.target.files);
    if (warning) {
      alert(warning);
      return;
    }

    const validFiles = selectedFiles.filter(file =>
      file.type.startsWith('image/') || file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );
    
    if (validFiles.length === 0) return;

    try {
      const compressedFiles = await compressImages(validFiles);
      const renamedFiles = compressedFiles.map(file => renameFile(file));
      
      const newPhotos = renamedFiles.map(file => ({
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        name: file.name
      }));
      
      setDialogPhotos(prev => [...prev, ...newPhotos]);
    } catch (error) {
      console.error('Error processing attachments:', error);
      alert('Error processing attachments');
    }
  }, []);

  const removeDialogPhoto = useCallback((index) => {
    setDialogPhotos(prev => {
      const updated = [...prev];
      if (updated[index]?.preview) {
        URL.revokeObjectURL(updated[index].preview);
      }
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  const handleEditActivity = (activity) => {
    setEditingActivityId(activity.activity_id);
    setNewActivityData({
      title: activity.title,
      description: activity.description || '',
      due_date: activity.due_date ? new Date(activity.due_date) : null,
      is_completed: activity.is_completed,
      assigned_workers: (activity.assigned_workers || [])
        .map(aw => aw?.worker_id)
        .filter(id => id != null),
      existing_photos: activity.photos || [],
      existing_documents: activity.documents || [],
      pending_file_links: [],
      linked_forms: activity.forms || [],
      pending_form_templates: [],
      removed_photo_ids: [],
      removed_document_ids: [],
      removed_form_ids: [],
    });
    setActivityDialogOpen(true);
  };

  const handleDeleteActivity = (activity) => {
    setActivityToDelete(activity);
    setActivityDeleteDialogOpen(true);
  };

  const handleConfirmDeleteActivity = async () => {
    if (!activityToDelete || deletingActivity) return;
    
    try {
      setDeletingActivity(true);
      await activitiesApi.delete(activityToDelete.activity_id);
      
      // Reload pins
      if (currentFileId) {
        const pinsResponse = await activitiesApi.getByFileId(currentFileId);
        setPins(pinsResponse.data || []);
      }
      
      setActivityDeleteDialogOpen(false);
      setActivityToDelete(null);
      setSelectedPin(null);
    } catch (error) {
      console.error('Error deleting activity:', error);
    } finally {
      setDeletingActivity(false);
    }
  };

  const handleSaveActivity = async () => {
    if (!newActivityData.title.trim()) {
      alert('Please enter an activity title');
      return;
    }

    setLoading(true);
    try {
      let activityId = editingActivityId;
      
      if (editingActivityId) {
        // Update existing activity
        const activityData = {
          title: newActivityData.title,
          description: newActivityData.description,
          due_date: newActivityData.due_date,
          is_completed: newActivityData.is_completed,
        };
        
        await activitiesApi.update(editingActivityId, activityData);
        
        // Update workers
        const validWorkerIds = (newActivityData.assigned_workers || []).filter(id => id != null);
        if (validWorkerIds.length > 0) {
             await activitiesApi.assignWorkers(editingActivityId, validWorkerIds);
        }
      } else {
        // Create new activity
        if (!pendingPin || !currentFileId) return;
        
        const activityData = {
          title: newActivityData.title,
          description: newActivityData.description,
          due_date: newActivityData.due_date,
          is_completed: newActivityData.is_completed,
          SourceFileId: currentFileId,
          PinX: pendingPin.x,
          PinY: pendingPin.y,
        };

        const response = await activitiesApi.create(projectId, activityData);
        activityId = response.data.activity_id;

        // Assign workers if any are selected
        const newValidWorkerIds = (newActivityData.assigned_workers || []).filter(id => id != null);
        if (newValidWorkerIds.length > 0) {
          await activitiesApi.assignWorkers(activityId, newValidWorkerIds);
        }
      }

      // Upload photos if any
      if (dialogPhotos.length > 0 && activityId) {
        try {
          await Promise.all(dialogPhotos.map(photo => {
            const formData = new FormData();
            formData.append('file', photo.file);
            return activitiesApi.uploadPhoto(activityId, formData);
          }));
        } catch (photoError) {
          console.error('Error uploading photos:', photoError);
          // Continue even if photo upload fails
        }
      }

      if (newActivityData.pending_form_templates?.length > 0 && activityId) {
        try {
          await Promise.all(
            newActivityData.pending_form_templates.map((template) => {
              const initialFormData = {};
              (template.form_schema?.fields || []).forEach((field) => {
                initialFormData[field.id] = '';
              });

              return projectFormsApi.create(projectId, {
                template_id: template.template_id,
                form_data: initialFormData,
                activity_id: activityId,
              });
            })
          );
        } catch (formError) {
          console.error('Error linking activity forms:', formError);
        }
      }

      if (newActivityData.pending_file_links?.length > 0 && activityId) {
        await Promise.all(newActivityData.pending_file_links.map((file) =>
          activitiesApi.linkFile(activityId, file.file_id)
        ));
      }

      if (editingActivityId) {
        const removals = [
          ...((newActivityData.removed_photo_ids || []).map((photoId) => activitiesApi.deletePhoto(photoId))),
          ...((newActivityData.removed_document_ids || []).map((fileId) => {
            const document = pins.find((pin) => pin.activity_id === activityId)
              ?.documents?.find((item) => item.file_id === fileId);
            return document?.is_existing_file_link
              ? activitiesApi.unlinkFile(activityId, fileId)
              : filesApi.delete(fileId);
          })),
          ...((newActivityData.removed_form_ids || []).map((formId) => projectFormsApi.delete(projectId, formId))),
        ];

        if (removals.length > 0) {
          await Promise.all(removals);
        }
      }

      // Reload pins for current file
      const pinsResponse = await activitiesApi.getByFileId(currentFileId);
      setPins(pinsResponse.data || []);

      handleCloseActivityDialog();
      setPinMode(false);
    } catch (error) {
      console.error('Error saving activity:', error);
      alert('Failed to save activity. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get current file to check if it's a plan
  const currentFile = allPreviewFiles[currentPreviewIndex];
  const isPlanImage = currentFile?.file_type === 'plans';
  const controlPanelSx = (theme) => ({
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
    gap: 1,
    mb: 2,
    p: 1.25,
    bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.88 : 0.96),
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: theme.palette.mode === 'dark'
      ? `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.04)}`
      : `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.7)}`,
  });
  const controlInputSx = (isActive = false) => (theme) => ({
    '& .MuiOutlinedInput-root': {
      bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.7 : 1),
      color: 'text.primary',
      borderRadius: 1.5,
      fontSize: '0.8125rem',
      transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow']),
      '& fieldset': {
        borderColor: isActive ? 'primary.main' : 'divider',
      },
      '&:hover fieldset': {
        borderColor: 'primary.main',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'primary.main',
      },
    },
    '& .MuiOutlinedInput-input': {
      py: 0.75,
      fontSize: '0.8125rem',
      color: 'text.primary',
      '&::placeholder': {
        color: 'text.secondary',
        opacity: 1,
      },
      '&::-webkit-calendar-picker-indicator': {
        cursor: 'pointer',
        filter: theme.palette.mode === 'dark' ? 'invert(1) brightness(0.85)' : 'none',
      },
    },
    '& .MuiInputAdornment-root': {
      color: 'text.secondary',
    },
  });

  const getFoldersByType = (fileType) => (
    documentFolders.filter((folder) => folder.file_type === fileType)
  );

  const getFileFolderId = (file) => (
    file.document_folder_id ?? file.photo_folder_id ?? null
  );

  const renderFolderContents = (fileType) => {
    const sectionFiles = getFilesByType(fileType);
    const sectionFolders = getFoldersByType(fileType);
    const filtersActive = Boolean(searchTerm || uploadStartDateFilter || uploadEndDateFilter);
    const activeFolder = sectionFolders.find(
      (folder) => folder.document_folder_id === selectedFolderIds[fileType]
    );
    const sectionName = formatFileTypeName(fileType);
    const itemName = fileType === 'photos' ? 'photo' : 'file';

    if (filtersActive) {
      return (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Matching {itemName}s across all folders
          </Typography>
          <FolderView
            title={fileType}
            files={sectionFiles}
            onPreview={handlePreview}
            onDownload={handleDownload}
            onDelete={handleOpenDeleteDialog}
            onRename={handleOpenRenameDialog}
            onMove={handleOpenMoveFileDialog}
            onOpenActivity={onOpenActivity}
            onOpenForm={onOpenForm}
            getFileUrl={getFileUrl}
            searchTerm={searchTerm}
            uploadStartDateFilter={uploadStartDateFilter}
            uploadEndDateFilter={uploadEndDateFilter}
            viewMode={viewMode}
          />
        </Box>
      );
    }

    if (activeFolder) {
      const folderFiles = sectionFiles.filter(
        (file) => getFileFolderId(file) === activeFolder.document_folder_id
      );
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setSelectedFolderIds((current) => ({
                ...current,
                [fileType]: null,
              }))}
            >
              {sectionName}
            </Button>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
              {activeFolder.name} ({folderFiles.length})
            </Typography>
            <Button
              size="small"
              startIcon={<CloudUploadIcon />}
              onClick={() => handleOpenUploadDialog(fileType, activeFolder.document_folder_id)}
            >
              Upload here
            </Button>
          </Box>
          <FolderView
            title={fileType}
            files={folderFiles}
            onPreview={handlePreview}
            onDownload={handleDownload}
            onDelete={handleOpenDeleteDialog}
            onRename={handleOpenRenameDialog}
            onMove={handleOpenMoveFileDialog}
            onOpenActivity={onOpenActivity}
            onOpenForm={onOpenForm}
            getFileUrl={getFileUrl}
            searchTerm=""
            uploadStartDateFilter=""
            uploadEndDateFilter=""
            viewMode={viewMode}
            emptyMessage={`No ${itemName}s in this folder`}
          />
        </Box>
      );
    }

    const unfiledFiles = sectionFiles.filter((file) => getFileFolderId(file) == null);
    return (
      <Box>
        {sectionFolders.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Folders</Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 1.25,
                mb: 2.5,
              }}
            >
              {sectionFolders.map((folder) => {
                const fileCount = sectionFiles.filter(
                  (file) => getFileFolderId(file) === folder.document_folder_id
                ).length;
                return (
                  <Paper
                    key={folder.document_folder_id}
                    data-testid={`document-folder-${folder.document_folder_id}`}
                    onClick={() => setSelectedFolderIds((current) => ({
                      ...current,
                      [fileType]: folder.document_folder_id,
                    }))}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                    }}
                  >
                    <FolderIcon color="primary" />
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography noWrap variant="body2" sx={{ fontWeight: 600 }}>
                        {folder.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fileCount} {fileCount === 1 ? itemName : `${itemName}s`}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      title={`Rename ${folder.name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenRenameFolderDialog(folder);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title={`Delete ${folder.name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenDeleteFolderDialog(folder);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                );
              })}
            </Box>
          </>
        )}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {sectionName} without a folder ({unfiledFiles.length})
        </Typography>
        <FolderView
          title={fileType}
          files={unfiledFiles}
          onPreview={handlePreview}
          onDownload={handleDownload}
          onDelete={handleOpenDeleteDialog}
          onRename={handleOpenRenameDialog}
          onMove={handleOpenMoveFileDialog}
          onOpenActivity={onOpenActivity}
          onOpenForm={onOpenForm}
          getFileUrl={getFileUrl}
          searchTerm=""
          uploadStartDateFilter=""
          uploadEndDateFilter=""
          viewMode={viewMode}
          emptyMessage={`No ${itemName}s without a folder`}
        />
      </Box>
    );
  };

  const renderFolderSelectValue = (value, fileType) => {
    if (value === '' || value === null || value === undefined) return 'No folder';
    return getFoldersByType(fileType)
      .find((folder) => folder.document_folder_id === Number(value))?.name || 'No folder';
  };

  return (
    <Box sx={{ height: '100%', width: '100%', p: { xs: 1, md: 2 } }}>
      {/* Loading Dialog */}
      <Dialog open={loading} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>Loading...</Typography>
        </Box>
      </Dialog>
      
      {/* Header with search and view controls */}
      <Box sx={controlPanelSx}>
        {/* Search */}
        <TextField
          size="small"
          placeholder="Search files..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '17px', color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')} edge="end" sx={{ p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: '15px' }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={[
            controlInputSx(),
            {
              flexGrow: 1,
              minWidth: 160,
            },
          ]}
        />

        {/* Date range */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, flexWrap: 'wrap' }}>
          <CalendarTodayIcon sx={{ fontSize: '15px', color: 'text.secondary', ml: 0.5 }} />
          <TextField
            size="small"
            type="date"
            value={uploadStartDateFilter}
            onChange={(e) => setUploadStartDateFilter(e.target.value)}
            inputProps={{ 'aria-label': 'Upload date from' }}
            sx={[
              controlInputSx(Boolean(uploadStartDateFilter)),
              {
                width: 148,
              },
            ]}
          />
          <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, px: 0.25 }}>—</Typography>
          <TextField
            size="small"
            type="date"
            value={uploadEndDateFilter}
            onChange={(e) => setUploadEndDateFilter(e.target.value)}
            inputProps={{ 'aria-label': 'Upload date to' }}
            sx={[
              controlInputSx(Boolean(uploadEndDateFilter)),
              {
                width: 148,
              },
            ]}
          />
          {(uploadStartDateFilter || uploadEndDateFilter) && (
            <IconButton
              size="small"
              onClick={() => { setUploadStartDateFilter(''); setUploadEndDateFilter(''); }}
              title="Clear date filter"
              sx={{
                p: 0.5,
                color: 'text.secondary',
                '&:hover': { color: 'error.main', bgcolor: 'action.hover' },
              }}
            >
              <CloseIcon sx={{ fontSize: '15px' }} />
            </IconButton>
          )}
        </Box>

        {/* View toggle */}
        <IconButton
          size="small"
          onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
          title={`Switch to ${viewMode === 'grid' ? 'list' : 'grid'} view`}
          sx={{
            p: 0.75,
            bgcolor: (theme) => alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.7 : 1),
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            flexShrink: 0,
            alignSelf: { xs: 'flex-end', sm: 'auto' },
            '&:hover': { bgcolor: 'primary.main', color: 'white', borderColor: 'primary.main' },
            transition: 'all 0.15s ease',
          }}
        >
          {viewMode === 'grid' ? <ViewListIcon sx={{ fontSize: '18px' }} /> : <GridViewIcon sx={{ fontSize: '18px' }} />}
        </IconButton>
      </Box>

      {/* Folder structure with accordions */}
      {fileTypes.map((type) => (
        <Accordion key={type} sx={{ mb: 1.5 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5, minHeight: 'auto', '&.Mui-expanded': { minHeight: 'auto' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexGrow: 1 }}>
              <FolderIcon color="primary" sx={{ fontSize: '20px' }} />
              <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', fontWeight: 600, fontSize: '0.95rem' }}>
                {formatFileTypeName(type)} ({getFilesByType(type).length})
              </Typography>
            </Box>
            <Button
              size="small"
              startIcon={<CloudUploadIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenUploadDialog(type);
              }}
              sx={{ mr: 1 }}
            >
              Upload
            </Button>
            <Button
              size="small"
              startIcon={<CreateNewFolderIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenCreateFolderDialog(type);
              }}
              sx={{ mr: 1 }}
            >
              New folder
            </Button>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 1.5, px: 1 }}>
            {renderFolderContents(type)}
          </AccordionDetails>
        </Accordion>
      ))}
      
      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload {formatFileTypeName(uploadType)}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Folder (optional)"
            value={uploadFolderId}
            onChange={(event) => setUploadFolderId(event.target.value)}
            helperText={`${formatFileTypeName(uploadType)} are uploaded without a folder by default.`}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (value) => renderFolderSelectValue(value, uploadType),
            }}
          >
            <MenuItem value="">No folder</MenuItem>
            {getFoldersByType(uploadType).map((folder) => (
              <MenuItem key={folder.document_folder_id} value={folder.document_folder_id}>
                {folder.name}
              </MenuItem>
            ))}
          </TextField>
          <UploadDropzone onDrop={handleFileDrop} fileType={uploadType} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={folderDialogOpen} onClose={handleCloseFolderDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {folderDialogMode === 'rename'
            ? 'Rename folder'
            : `New ${formatFileTypeName(folderFileType)} folder`}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            autoFocus
            label="Folder name"
            value={folderName}
            onChange={(event) => {
              setFolderName(event.target.value);
              setFolderError('');
            }}
            error={Boolean(folderError)}
            helperText={folderError}
            inputProps={{ maxLength: 100 }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSaveFolder();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFolderDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFolder} disabled={!folderName.trim()}>
            {folderDialogMode === 'rename' ? 'Rename' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteFolderDialogOpen} onClose={() => setDeleteFolderDialogOpen(false)}>
        <DialogTitle>Delete folder?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete “{selectedFolder?.name}”? Its files will not be deleted; they will return to the {formatFileTypeName(selectedFolder?.file_type)} section without a folder.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFolderDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteFolder}>Delete folder</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={moveFileDialogOpen}
        onClose={() => setMoveFileDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Move file</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Folder"
            value={moveFolderId}
            onChange={(event) => setMoveFolderId(event.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (value) => renderFolderSelectValue(value, fileToMove?.file_type),
            }}
          >
            <MenuItem value="">No folder</MenuItem>
            {getFoldersByType(fileToMove?.file_type).map((folder) => (
              <MenuItem key={folder.document_folder_id} value={folder.document_folder_id}>
                {folder.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveFileDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMoveFile}>Move</Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedFile?.file_name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename File Dialog */}
      <Dialog open={renameDialogOpen} onClose={handleCloseRenameDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Rename File</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="File Name"
            value={newFileName}
            onChange={(e) => {
              setNewFileName(e.target.value);
              setRenameError('');
            }}
            error={!!renameError}
            helperText={renameError}
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !renameError) {
                handleRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRenameDialog}>Cancel</Button>
          <Button onClick={handleRename} color="primary" variant="contained" disabled={!!renameError || !newFileName.trim()}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Component - Only for image files */}
      {isImageFile(previewFileName) && (
        <ImageViewer
          open={previewDialogOpen}
          onClose={handleClosePreviewDialog}
          imageUrl={previewUrl}
          fileName={previewFileName}
          uploadedDate={previewUploadedDate}
          allImages={allPreviewFiles}
          currentIndex={currentPreviewIndex}
          onNavigate={navigatePreviewPhoto}
          onDelete={handlePreviewDelete}
          isLoading={loading}
          pins={pins}
          enablePinMode={pinMode}
          onPinClick={handlePinClick}
          onAddPin={handleAddPin}
          onTogglePinMode={handleTogglePinMode}
          isPlanImage={isPlanImage}
          selectedPin={selectedPin}
          onClosePinDetails={() => setSelectedPin(null)}
          workers={workers}
          onEditActivity={handleEditActivity}
          onDeleteActivity={handleDeleteActivity}
          onOpenPlan={(_activity, plan) => handlePreview(plan)}
          canShare={currentUser?.role === 'Office'}
          onOpenActivity={(activityId) => {
            handleClosePreviewDialog();
            onOpenActivity?.(activityId);
          }}
          onOpenForm={(formId) => {
            handleClosePreviewDialog();
            onOpenForm?.(formId);
          }}
        />
      )}

      {/* Activity Creation/Edit Dialog for Pins */}
      <ActivityDialog
        open={activityDialogOpen}
        onClose={handleCloseActivityDialog}
        onSubmit={handleSaveActivity}
        formData={newActivityData}
        onFormDataChange={setNewActivityData}
        workers={workers}
        formTemplates={formTemplates}
        projectFiles={files.filter((file) => file.file_type === 'plans' || file.file_type === 'photos')}
        documentFolders={documentFolders}
        photos={dialogPhotos}
        onPhotoSelect={handleDialogPhotoSelect}
        onPhotoRemove={removeDialogPhoto}
        submitting={loading}
        isEditing={!!editingActivityId}
      />

      {/* Activity Delete Confirmation Dialog */}
      <Dialog open={activityDeleteDialogOpen} onClose={() => setActivityDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete Activity</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{activityToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityDeleteDialogOpen(false)} disabled={deletingActivity}>Cancel</Button>
          <Button onClick={handleConfirmDeleteActivity} color="error" variant="contained" disabled={deletingActivity}>
            {deletingActivity ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDocumentsTab;
